import React from 'react'
import { useOllieAssistant } from './OllieAssistantContext'
import styled from 'styled-components/macro'
import useOllieAtLimit from 'src/hooks/useOllieAtLimit'
import { isOllieDraftAskBlocked } from 'src/utils/ollieAskLimit'

/**
 * `checkId` opens that finding's thread (feature-page "Why?"); `question`
 * goes into the composer as a draft (also inside the thread when both are
 * given), never sent for them. `ask='why'` asks the thread's Why? chip as
 * soon as it opens. At the message limit a draft-only link is hidden: the
 * question could not be sent.
 */
const OllieAskLink = ({
  question,
  checkId,
  ask = null,
  label = 'Ask Ollie about this page',
  className,
  'data-testid': testId,
  onOpen,
  quiet = false,
  openEmpty = false,
  toolbar = false,
}) => {
  const { openOllie, openOllieWithDraft, openOllieWithFinding } =
    useOllieAssistant()
  const atLimit = useOllieAtLimit()

  if (!question && !checkId && !openEmpty) {
    return null
  }
  if (isOllieDraftAskBlocked({ checkId, atLimit })) {
    return null
  }

  return (
    <LinkButton
      type='button'
      className={className}
      data-testid={testId}
      data-chrome={quiet || toolbar ? 'quiet' : undefined}
      $quiet={quiet}
      $toolbar={toolbar}
      onClick={() => {
        if (typeof onOpen === 'function') onOpen()
        if (checkId) {
          openOllieWithFinding(checkId, {
            draft: question,
            ...(ask ? { ask } : {}),
          })
        } else if (question) {
          openOllieWithDraft(question)
        } else {
          openOllie()
        }
      }}
    >
      {label}
    </LinkButton>
  )
}

const LinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  margin: ${({ $toolbar }) => ($toolbar ? '0' : '-8px 0 16px')};
  padding: ${({ $toolbar }) => ($toolbar ? '0.45rem 0.85rem' : '0')};
  border: ${({ theme, $toolbar }) =>
    $toolbar ? `1.5px solid ${theme.colours.panSilver || '#e0dfdf'}` : 'none'};
  border-radius: ${({ $toolbar }) => ($toolbar ? '8px' : '0')};
  background: ${({ theme, $toolbar }) =>
    $toolbar ? theme.colours.mayoWhite || '#fff' : 'none'};
  color: ${({ theme, $quiet }) =>
    $quiet ? theme.colours.oliveGrey : theme.colours.bbqBlack};
  font-size: ${({ $quiet, $toolbar }) =>
    $toolbar ? '0.9rem' : $quiet ? '13px' : '14px'};
  font-weight: ${({ $quiet, $toolbar }) =>
    $toolbar ? '600' : $quiet ? '500' : '600'};
  text-decoration: ${({ $toolbar }) => ($toolbar ? 'none' : 'underline')};
  text-underline-offset: 3px;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colours.oliveGrey};
  }
`

export default OllieAskLink
